import type { ITfoDiagnosticReportCustomControlData } from '../../../../../../common/validation/customControls/ophthalmology';
import { tfoConvertOldToNewScreeningStandard } from '../../../../../../common/customControls/tfoV1ConvertOldToNewScreeningStandard';
import { FormParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { FundusscreeningPolicyAndUrgencyReadFields } from './FundusscreeningPolicyAndUrgencyReadFields';
import { NewStandardInfo } from './NewStandardInfo';

interface ITfoV1DiagnosticReportReadCustomControlFieldsProps {
    customControlData: ITfoDiagnosticReportCustomControlData;
    hideAdvisedPolicy?: boolean;
}

export function TfoV1DiagnosticReportReadCustomControlFields(
    props: Readonly<ITfoV1DiagnosticReportReadCustomControlFieldsProps>
) {
    return (
        <>
            {hasScreeningResults(props.customControlData) && (
                <>
                    <NewStandardInfo />
                    <HorizontalFormGroupRead>
                        <FormLabel>{''}</FormLabel>
                        <InputContainer>
                            <FormLabel>OD</FormLabel>
                        </InputContainer>
                        <InputContainer>
                            <FormLabel>OS</FormLabel>
                        </InputContainer>
                    </HorizontalFormGroupRead>

                    {hasRetinopathieResults(props.customControlData) && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Retinopathie</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>
                                    {props.customControlData.retinopathie_od ??
                                        tfoConvertOldToNewScreeningStandard(
                                            props.customControlData.bevindingen_npdr_od
                                        )}
                                </FormParagraph>
                            </InputContainer>
                            <InputContainer>
                                <FormParagraph bold>
                                    {props.customControlData.retinopathie_os ??
                                        tfoConvertOldToNewScreeningStandard(
                                            props.customControlData.bevindingen_npdr_os
                                        )}
                                </FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                    {hasMaculopathieResults(props.customControlData) && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Maculopathie</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{props.customControlData.maculopathie_od}</FormParagraph>
                            </InputContainer>
                            <InputContainer>
                                <FormParagraph bold>{props.customControlData.maculopathie_os}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}
                </>
            )}

            {/* Common */}
            {(props.customControlData.andere_bevindingen_od_text ||
                props.customControlData.andere_bevindingen_os_text) && (
                <HorizontalFormGroupRead>
                    <FormLabel>Andere bevindingen</FormLabel>
                    {props.customControlData.andere_bevindingen_od_text && (
                        <InputContainer>
                            <FormParagraph bold>{props.customControlData.andere_bevindingen_od_text}</FormParagraph>
                        </InputContainer>
                    )}
                    {!props.customControlData.andere_bevindingen_od_text &&
                        props.customControlData.andere_bevindingen_os_text && (
                            <InputContainer>
                                <FormParagraph> {''}</FormParagraph>
                            </InputContainer>
                        )}
                    {props.customControlData.andere_bevindingen_os_text && (
                        <InputContainer>
                            <FormParagraph bold>{props.customControlData.andere_bevindingen_os_text}</FormParagraph>
                        </InputContainer>
                    )}
                </HorizontalFormGroupRead>
            )}

            {!props.hideAdvisedPolicy && (
                <FundusscreeningPolicyAndUrgencyReadFields customControlData={props.customControlData} />
            )}

            {props.customControlData.toelichting && (
                <HorizontalFormGroupRead>
                    <FormLabel>Toelichting</FormLabel>
                    <InputContainer>
                        <FormParagraph bold>{props.customControlData.toelichting}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}
        </>
    );
}

function hasScreeningResults({
    bevindingen_npdr_od,
    bevindingen_npdr_os,
    retinopathie_od,
    retinopathie_os,
    maculopathie_od,
    maculopathie_os,
}: ITfoV1DiagnosticReportReadCustomControlFieldsProps['customControlData']) {
    return !!(
        bevindingen_npdr_od ??
        bevindingen_npdr_os ??
        retinopathie_od ??
        retinopathie_os ??
        maculopathie_od ??
        maculopathie_os
    );
}

function hasRetinopathieResults({
    bevindingen_npdr_od,
    bevindingen_npdr_os,
    retinopathie_od,
    retinopathie_os,
}: ITfoV1DiagnosticReportReadCustomControlFieldsProps['customControlData']) {
    return !!(bevindingen_npdr_od ?? bevindingen_npdr_os ?? retinopathie_od ?? retinopathie_os);
}

function hasMaculopathieResults({
    maculopathie_od,
    maculopathie_os,
}: ITfoV1DiagnosticReportReadCustomControlFieldsProps['customControlData']) {
    return !!(maculopathie_od ?? maculopathie_os);
}
